import React, { useState, useCallback, useMemo, useRef } from "react";
import { db } from "./firebaseConfig";
import "./ViewQuizResponses.css";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  documentId,
  writeBatch,
  runTransaction,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

// Use the environment variable for the backend URL.
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ViewQuizResponses = ({ user }) => {
  const [quizCode, setQuizCode] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [manualMarks, setManualMarks] = useState({});
  const [markingProgress, setMarkingProgress] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: false,
    currentBatch: 0,
    totalBatches: 0,
    retryAttempts: 0,
    verificationPhase: false,
    verificationCompleted: 0,
    verificationFailed: 0
  });
  
  // Ref to track processed submissions for verification
  const processedSubmissionsRef = useRef(new Set());
  
  // State to track total tokens used during AI marking
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  
  // New state for UI enhancements
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("name-asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [quizTitle, setQuizTitle] = useState("");
  const [expandedStudents, setExpandedStudents] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [userCache, setUserCache] = useState({});
  const [questionCache, setQuestionCache] = useState({});

  // Configuration constants for batch processing - optimized values
  const BATCH_SIZE = 20; // Increased from 15 for better throughput
  const DELAY_BETWEEN_BATCHES = 500; // Reduced from 800ms for faster processing
  const MAX_RETRIES = 3; // Reduced from 5 - we'll handle failures more systematically
  const FIRESTORE_BATCH_LIMIT = 10; // Firestore has a limit of 10 items per batch get
  const VERIFICATION_BATCH_SIZE = 30; // Larger batch size for verification phase

  const navigate = useNavigate();

  // Load quiz responses from Firestore with optimized batch fetching
  const loadQuizResponses = async () => {
    if (!quizCode) {
      alert("Please enter a quiz code!");
      return;
    }

    try {
      setLoading(true);
      setSubmissions([]);
      setExpandedStudents({});
      setExpandedQuestions({});
      
      // Reset caches
      setUserCache({});
      setQuestionCache({});

      // First, try to get the quiz title
      try {
        const quizzesRef = collection(db, "quizzes");
        const quizzesQuery = query(quizzesRef, where("code", "==", quizCode));
        const quizzesSnapshot = await getDocs(quizzesQuery);
        
        if (!quizzesSnapshot.empty) {
          const quizData = quizzesSnapshot.docs[0].data();
          setQuizTitle(quizData.title || "");
        }
      } catch (error) {
        console.error("Error fetching quiz title:", error);
      }

      // Fetch all submissions for this quiz code
      const submissionsRef = collection(db, "submissions");
      const submissionsQuery = query(
        submissionsRef,
        where("quizCode", "==", quizCode)
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);
      
      if (submissionsSnapshot.empty) {
        setLoading(false);
        return;
      }

      // Extract unique user IDs and question IDs for batch fetching
      const userIds = new Set();
      const questionIds = new Set();
      const submissionDocs = [];

      submissionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.questionId) return;
        
        userIds.add(data.userId);
        
        // Extract question ID (handles paths like "questions/someID")
        const extractedQuestionId = data.questionId.includes("/")
          ? data.questionId.split("/").pop()
          : data.questionId;
          
        questionIds.add(extractedQuestionId);
        
        submissionDocs.push({
          id: doc.id,
          ...data,
          extractedQuestionId
        });
      });

      // Batch fetch user data
      const userDataMap = {};
      const userIdArray = Array.from(userIds);
      
      for (let i = 0; i < userIdArray.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = userIdArray.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const usersQuery = query(collection(db, "users"), where(documentId(), "in", batch));
        const usersSnapshot = await getDocs(usersQuery);
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          userDataMap[doc.id] = {
            username: userData.username || userData.displayName || "Unknown Student"
          };
        });
      }
      
      // Handle any missing users
      userIdArray.forEach(userId => {
        if (!userDataMap[userId]) {
          userDataMap[userId] = { username: "Unknown Student" };
        }
      });
      
      // Update user cache
      setUserCache(userDataMap);

      // Batch fetch question data
      const questionDataMap = {};
      const questionIdArray = Array.from(questionIds);
      
      for (let i = 0; i < questionIdArray.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = questionIdArray.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const questionsQuery = query(collection(db, "questions"), where(documentId(), "in", batch));
        const questionsSnapshot = await getDocs(questionsQuery);
        
        questionsSnapshot.docs.forEach(doc => {
          questionDataMap[doc.id] = doc.data();
        });
      }
      
      // Update question cache
      setQuestionCache(questionDataMap);

      // Process submissions with the fetched data
      const responses = submissionDocs.map(submission => {
        const questionData = questionDataMap[submission.extractedQuestionId] || {};
        if (!questionData) return null; // Skip if question data not found
        
        const userData = userDataMap[submission.userId] || { username: "Unknown Student" };
        
        // For AI grading payload, if no question text exists, default to "Image-based question"
        const questionTextForAI =
          questionData.questionText && questionData.questionText.trim() !== ""
            ? questionData.questionText
            : (questionData.questionImageURL ? "Image-based question" : "Unknown Question");

        // For display purposes, if text exists use it; otherwise default to "Image-based question"
        const questionDisplay =
          questionData.questionText && questionData.questionText.trim() !== ""
            ? questionData.questionText
            : "Image-based question";

        return {
          id: submission.id,
          userId: submission.userId,
          username: userData.username,
          questionTextForAI,
          questionDisplay,
          questionImageURL: questionData.questionImageURL || "",
          userAnswer: submission.userAnswer,
          markSchemeAnswer: questionData.markSchemeAnswer || "N/A",
          feedback: submission.feedback ?? "Pending",
          maxMark: questionData.maxMark ?? "N/A",
          markAwarded: submission.markAwarded ?? "Pending",
          submittedAt: submission.submittedAt.toDate().toLocaleString(),
        };
      }).filter(Boolean); // Remove any null entries

      setSubmissions(responses);
      
      // Initialize manualMarks state based on the fetched responses
      const newManualMarks = {};
      responses.forEach((response) => {
        newManualMarks[response.id] = response.markAwarded;
      });
      setManualMarks(newManualMarks);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching quiz responses:", error);
      alert("Failed to load quiz responses. Please try again.");
      setLoading(false);
    }
  };

  // Enhanced batch processing with improved retry logic and tracking
  const processBatchWithRetry = useCallback(async (batch, maxRetries, isRetry = false) => {
    let retryAttempt = 0;
    let successful = [];
    let failed = [];
    let currentBatch = [...batch];
    
    // Create a map to track individual submission status and errors
    const submissionStatus = new Map();
    batch.forEach(sub => {
      submissionStatus.set(sub.id, { 
        processed: false, 
        attempts: 0, 
        error: null 
      });
    });

    while (retryAttempt <= maxRetries && currentBatch.length > 0) {
      try {
        if (retryAttempt > 0 || isRetry) {
          setMarkingProgress(prev => ({
            ...prev,
            retryAttempts: prev.retryAttempts + 1
          }));
        }

        // Log batch size for debugging
        console.log(`Processing batch of ${currentBatch.length} submissions (attempt ${retryAttempt + 1})`);
        
        const response = await fetch(`${backendUrl}api/mark-quiz`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ submissions: currentBatch }),
        });

        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }

        const responseData = await response.json();
        const processedSubmissions = responseData.submissions || responseData;
        
        // Track token usage if available
        if (responseData.usage && responseData.usage.total_tokens) {
          setTotalTokensUsed(prev => prev + responseData.usage.total_tokens);
        }
        
        // Use transaction-based approach for Firestore updates to ensure consistency
        await updateFirestoreWithTransaction(processedSubmissions);
        
        // Track successfully processed submissions
        processedSubmissions.forEach(sub => {
          processedSubmissionsRef.current.add(sub.id);
          if (submissionStatus.has(sub.id)) {
            submissionStatus.get(sub.id).processed = true;
          }
        });
        
        successful = [...successful, ...processedSubmissions];
        currentBatch = [];
      } catch (error) {
        console.error(`Batch processing error (attempt ${retryAttempt + 1}/${maxRetries + 1}):`, error);
        
        // Increment attempt count for all submissions in this batch
        currentBatch.forEach(sub => {
          if (submissionStatus.has(sub.id)) {
            const status = submissionStatus.get(sub.id);
            status.attempts++;
            status.error = error.message;
          }
        });
        
        if (retryAttempt === maxRetries) {
          // On final retry, log detailed failure information
          console.error("Failed submissions after max retries:", 
            currentBatch.map(sub => ({
              id: sub.id,
              attempts: submissionStatus.get(sub.id)?.attempts || 0,
              error: submissionStatus.get(sub.id)?.error || "Unknown error"
            }))
          );
          
          failed = [...failed, ...currentBatch];
          currentBatch = [];
        } else if (currentBatch.length > 3) {
          // Split batch in half for better error isolation
          const halfIndex = Math.ceil(currentBatch.length / 2);
          const firstHalf = currentBatch.slice(0, halfIndex);
          const secondHalf = currentBatch.slice(halfIndex);
          
          console.log(`Splitting batch into smaller chunks: ${firstHalf.length} and ${secondHalf.length}`);
          
          // Process first half
          const firstHalfResults = await processBatchWithRetry(
            firstHalf, 
            maxRetries - retryAttempt,
            true
          );
          
          // Process second half
          const secondHalfResults = await processBatchWithRetry(
            secondHalf, 
            maxRetries - retryAttempt,
            true
          );
          
          successful = [...successful, ...firstHalfResults.successful, ...secondHalfResults.successful];
          failed = [...failed, ...firstHalfResults.failed, ...secondHalfResults.failed];
          currentBatch = [];
        } else {
          // For very small batches, try one by one as a last resort
          if (currentBatch.length > 1) {
            const oneByOneResults = await processOneByOne(currentBatch);
            successful = [...successful, ...oneByOneResults.successful];
            failed = [...failed, ...oneByOneResults.failed];
            currentBatch = [];
          } else {
            retryAttempt++;
            // Exponential backoff with jitter for retries
            const baseDelay = 1000 * Math.pow(1.5, retryAttempt);
            const jitter = Math.random() * 500;
            const delay = Math.min(baseDelay + jitter, 8000);
            console.log(`Retrying single submission after ${delay}ms delay`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    }

    return { successful, failed };
  }, []);

  // Enhanced one-by-one processing with better error handling and tracking
  const processOneByOne = useCallback(async (submissions) => {
    const successful = [];
    const failed = [];
    
    console.log(`Processing ${submissions.length} submissions one by one`);

    for (const submission of submissions) {
      try {
        const response = await fetch(`${backendUrl}api/mark-quiz`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ submissions: [submission] }),
        });

        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }

        const [processedSubmission] = await response.json();
        
        // Use transaction for individual updates too
        try {
          await runTransaction(db, async (transaction) => {
            const submissionRef = doc(db, "submissions", processedSubmission.id);
            transaction.update(submissionRef, {
              feedback: processedSubmission.feedback,
              markAwarded: processedSubmission.markAwarded,
            });
          });
          
          // Track successfully processed submission
          processedSubmissionsRef.current.add(processedSubmission.id);
          
          successful.push(processedSubmission);
          
          setMarkingProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
        } catch (firestoreError) {
          console.error(`Firestore transaction error for submission ${processedSubmission.id}:`, firestoreError);
          failed.push(submission);
          
          setMarkingProgress(prev => ({
            ...prev,
            failed: prev.failed + 1
          }));
        }
      } catch (error) {
        console.error(`Error processing submission ${submission.id}:`, error);
        failed.push(submission);
        
        setMarkingProgress(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }
      
      // Shorter delay between individual submissions
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    return { successful, failed };
  }, []);

  // Transaction-based Firestore update for better consistency
  const updateFirestoreWithTransaction = useCallback(async (submissions) => {
    const FIRESTORE_BATCH_SIZE = 500; // Firestore batch write limit is 500
    
    for (let i = 0; i < submissions.length; i += FIRESTORE_BATCH_SIZE) {
      const batchItems = submissions.slice(i, i + FIRESTORE_BATCH_SIZE);
      
      try {
        // Use a transaction for atomic updates
        await runTransaction(db, async (transaction) => {
          batchItems.forEach(sub => {
            const submissionRef = doc(db, "submissions", sub.id);
            transaction.update(submissionRef, {
              feedback: sub.feedback,
              markAwarded: sub.markAwarded,
            });
          });
        });
        
        console.log(`Successfully updated ${batchItems.length} submissions in Firestore`);
      } catch (error) {
        console.error(`Transaction error updating Firestore batch:`, error);
        
        // Fall back to batch writes if transaction fails
        try {
          const writeBatchObj = writeBatch(db);
          
          batchItems.forEach(sub => {
            const submissionRef = doc(db, "submissions", sub.id);
            writeBatchObj.update(submissionRef, {
              feedback: sub.feedback,
              markAwarded: sub.markAwarded,
            });
          });
          
          await writeBatchObj.commit();
          console.log(`Successfully updated ${batchItems.length} submissions using batch fallback`);
        } catch (batchError) {
          console.error(`Batch fallback error:`, batchError);
          throw batchError; // Re-throw to handle at higher level
        }
      }
    }
  }, []);

  // Enhanced state update with validation
  const updateSubmissionsState = useCallback((processedSubmissions) => {
    setSubmissions(prevSubmissions => {
      const updatedSubmissions = [...prevSubmissions];
      
      processedSubmissions.forEach(processed => {
        const index = updatedSubmissions.findIndex(s => s.id === processed.id);
        if (index !== -1) {
          updatedSubmissions[index] = {
            ...updatedSubmissions[index],
            feedback: processed.feedback,
            markAwarded: processed.markAwarded
          };
        } else {
          console.warn(`Submission ${processed.id} not found in local state during update`);
        }
      });
      
      return updatedSubmissions;
    });
    
    setManualMarks(prevMarks => {
      const newMarks = { ...prevMarks };
      processedSubmissions.forEach(sub => {
        newMarks[sub.id] = sub.markAwarded;
      });
      return newMarks;
    });
  }, []);

  // Verify all submissions were processed and retry any missed ones
  const verifyAndRetryMissedSubmissions = useCallback(async (allSubmissions) => {
    try {
      setMarkingProgress(prev => ({
        ...prev,
        verificationPhase: true,
        currentBatch: 1,
        totalBatches: 1
      }));
      
      console.log("Starting verification phase to check for missed submissions");
      
      // Find submissions that weren't processed
      const processedIds = processedSubmissionsRef.current;
      const missedSubmissions = allSubmissions.filter(sub => !processedIds.has(sub.id));
      
      if (missedSubmissions.length === 0) {
        console.log("Verification complete: All submissions were processed successfully");
        return { successful: [], failed: [] };
      }
      
      console.log(`Verification found ${missedSubmissions.length} missed submissions to retry`);
      
      setMarkingProgress(prev => ({
        ...prev,
        verificationCompleted: 0,
        verificationFailed: 0
      }));
      
      // Process missed submissions in larger batches
      const verificationBatches = [];
      for (let i = 0; i < missedSubmissions.length; i += VERIFICATION_BATCH_SIZE) {
        verificationBatches.push(missedSubmissions.slice(i, i + VERIFICATION_BATCH_SIZE));
      }
      
      setMarkingProgress(prev => ({
        ...prev,
        totalBatches: verificationBatches.length
      }));
      
      const verificationSuccessful = [];
      const verificationFailed = [];
      
      // Process each verification batch
      for (let batchIndex = 0; batchIndex < verificationBatches.length; batchIndex++) {
        setMarkingProgress(prev => ({
          ...prev,
          currentBatch: batchIndex + 1
        }));
        
        const currentBatch = verificationBatches[batchIndex];
        
        const batchResults = await processBatchWithRetry(
          currentBatch, 
          MAX_RETRIES,
          true
        );
        
        verificationSuccessful.push(...batchResults.successful);
        verificationFailed.push(...batchResults.failed);
        
        setMarkingProgress(prev => ({
          ...prev,
          verificationCompleted: verificationSuccessful.length,
          verificationFailed: verificationFailed.length
        }));
        
        updateSubmissionsState(batchResults.successful);
        
        if (batchIndex < verificationBatches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }
      
      return { successful: verificationSuccessful, failed: verificationFailed };
    } catch (error) {
      console.error("Error during verification phase:", error);
      return { successful: [], failed: [] };
    } finally {
      setMarkingProgress(prev => ({
        ...prev,
        verificationPhase: false
      }));
    }
  }, [VERIFICATION_BATCH_SIZE, DELAY_BETWEEN_BATCHES, MAX_RETRIES, processBatchWithRetry, updateSubmissionsState]);
  
  // Reload submissions from Firestore to ensure UI state matches database
  const reloadSubmissionsFromFirestore = useCallback(async () => {
    try {
      console.log("Reloading submissions from Firestore to ensure state consistency");
      
      // Get all submission IDs currently in state
      const submissionIds = submissions.map(sub => sub.id);
      
      if (submissionIds.length === 0) return;
      
      // Fetch submissions in batches to avoid Firestore limits
      const refreshedSubmissions = [];
      
      for (let i = 0; i < submissionIds.length; i += FIRESTORE_BATCH_LIMIT) {
        const batchIds = submissionIds.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const submissionsQuery = query(
          collection(db, "submissions"), 
          where(documentId(), "in", batchIds)
        );
        
        const submissionsSnapshot = await getDocs(submissionsQuery);
        
        submissionsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          refreshedSubmissions.push({
            id: doc.id,
            feedback: data.feedback,
            markAwarded: data.markAwarded
          });
        });
      }
      
      // Update local state with fresh data from Firestore
      setSubmissions(prevSubmissions => {
        const updatedSubmissions = [...prevSubmissions];
        
        refreshedSubmissions.forEach(refreshed => {
          const index = updatedSubmissions.findIndex(s => s.id === refreshed.id);
          if (index !== -1) {
            updatedSubmissions[index] = {
              ...updatedSubmissions[index],
              feedback: refreshed.feedback,
              markAwarded: refreshed.markAwarded
            };
          }
        });
        
        return updatedSubmissions;
      });
      
      // Update manual marks state
      setManualMarks(prevMarks => {
        const newMarks = { ...prevMarks };
        refreshedSubmissions.forEach(sub => {
          newMarks[sub.id] = sub.markAwarded;
        });
        return newMarks;
      });
      
      console.log(`Successfully reloaded ${refreshedSubmissions.length} submissions from Firestore`);
      console.log(`Total tokens used: ${totalTokensUsed}`);
    } catch (error) {
      console.error("Error reloading submissions from Firestore:", error);
    }
  }, [submissions]);

  // Enhanced marking function with verification and state reconciliation
  const markSubmissionsWithAI = useCallback(async (submissionsToMark) => {
    if (submissions.length === 0) {
      alert("No responses to mark!");
      return;
    }

    if (submissionsToMark.length === 0) {
      const confirmRemark = window.confirm("All submissions are already marked, are you sure you want to mark again?");
      if (!confirmRemark) {
        return;
      }
    }

    // Reset the processed submissions tracking
    processedSubmissionsRef.current = new Set();
    
    // Reset token counter for this marking session
    setTotalTokensUsed(0);
    
    setMarking(true);
    
    const preparedSubmissions = submissionsToMark.map(sub => ({
      ...sub,
      questionText: sub.questionTextForAI,
    }));
    
    // Create batches for processing
    const batches = [];
    for (let i = 0; i < preparedSubmissions.length; i += BATCH_SIZE) {
      batches.push(preparedSubmissions.slice(i, i + BATCH_SIZE));
    }
    
    setMarkingProgress({
      total: preparedSubmissions.length,
      completed: 0,
      failed: 0,
      inProgress: true,
      currentBatch: 0,
      totalBatches: batches.length,
      retryAttempts: 0,
      verificationPhase: false,
      verificationCompleted: 0,
      verificationFailed: 0
    });

    try {
      console.log(`Starting to process ${preparedSubmissions.length} submissions in ${batches.length} batches`);
      
      const successfulSubmissions = [];
      const failedSubmissions = [];
      
      // Process each batch
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        setMarkingProgress(prev => ({
          ...prev,
          currentBatch: batchIndex + 1
        }));
        
        const currentBatch = batches[batchIndex];
        
        const batchResults = await processBatchWithRetry(
          currentBatch, 
          MAX_RETRIES
        );
        
        successfulSubmissions.push(...batchResults.successful);
        failedSubmissions.push(...batchResults.failed);
        
        setMarkingProgress(prev => ({
          ...prev,
          completed: successfulSubmissions.length,
          failed: failedSubmissions.length
        }));
        
        updateSubmissionsState(batchResults.successful);
        
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }
      
      // Retry failed submissions with increased priority
      if (failedSubmissions.length > 0) {
        console.log(`Retrying ${failedSubmissions.length} failed submissions`);
        
        setMarkingProgress(prev => ({
          ...prev,
          currentBatch: batches.length + 1,
          totalBatches: batches.length + 1
        }));
        
        const retryResults = await processBatchWithRetry(
          failedSubmissions, 
          MAX_RETRIES,
          true
        );
        
        successfulSubmissions.push(...retryResults.successful);
        
        updateSubmissionsState(retryResults.successful);
        
        setMarkingProgress(prev => ({
          ...prev,
          completed: prev.completed + retryResults.successful.length,
          failed: retryResults.failed.length
        }));
      }
      
      // Verification phase - check for any missed submissions
      console.log("Starting verification phase to ensure all submissions were processed");
      const verificationResults = await verifyAndRetryMissedSubmissions(preparedSubmissions);
      
      if (verificationResults.successful.length > 0) {
        console.log(`Verification phase successfully processed ${verificationResults.successful.length} missed submissions`);
        successfulSubmissions.push(...verificationResults.successful);
        updateSubmissionsState(verificationResults.successful);
      }
      
      // Final database reload to ensure UI state matches Firestore
      await reloadSubmissionsFromFirestore();
      
      // Show completion message
      if (failedSubmissions.length === 0 && verificationResults.failed.length === 0) {
        alert("AI grading complete! All submissions were processed successfully.");
      } else {
        const totalFailed = failedSubmissions.length + verificationResults.failed.length;
        alert(`Grading completed with ${totalFailed} failed submissions. The rest were graded successfully.`);
      }
      
    } catch (error) {
      console.error("Error with AI grading:", error);
      alert("Failed to mark responses with AI.");
    } finally {
      setMarking(false);
      setMarkingProgress(prev => ({ ...prev, inProgress: false }));
    }
  }, [
    submissions, 
    BATCH_SIZE, 
    DELAY_BETWEEN_BATCHES, 
    MAX_RETRIES, 
    processBatchWithRetry, 
    updateSubmissionsState, 
    verifyAndRetryMissedSubmissions, 
    reloadSubmissionsFromFirestore
  ]);

  // Mark ALL submissions
  const markAllWithAI = useCallback(() => {
    markSubmissionsWithAI(submissions);
  }, [submissions, markSubmissionsWithAI]);

  // Mark ONLY unmarked submissions
  const markAllUnmarkedWithAI = useCallback(() => {
    const unmarkedSubmissions = submissions.filter(
      (s) => s.markAwarded === "Pending" || s.markAwarded === "N/A"
    );
    markSubmissionsWithAI(unmarkedSubmissions);
  }, [submissions, markSubmissionsWithAI]);

  // Group and filter submissions by userId - optimized with useMemo
  const filteredAndSortedUsers = useMemo(() => {
    // Group submissions by user
    const groupedByUser = submissions.reduce((acc, sub) => {
      const { userId } = sub;
      if (!acc[userId]) {
        acc[userId] = {
          userId,
          username: sub.username,
          items: [],
        };
      }
      acc[userId].items.push(sub);
      return acc;
    }, {});

    let usersArray = Object.values(groupedByUser);

    // Pre-calculate scores for each user to avoid recalculating during sort
    usersArray = usersArray.map(user => {
      const totalMarks = user.items.reduce(
        (acc, s) => acc + (parseFloat(s.markAwarded) || 0), 0
      );
      const totalMaxMarks = user.items.reduce(
        (acc, s) => acc + (parseFloat(s.maxMark) || 0), 0
      );
      const scorePercentage = totalMaxMarks > 0 ? (totalMarks / totalMaxMarks) * 100 : 0;
      
      return {
        ...user,
        totalMarks,
        totalMaxMarks,
        scorePercentage
      };
    });

    // Filter by search term
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      usersArray = usersArray.filter(user => 
        user.username.toLowerCase().includes(searchLower)
      );
    }

    // Sort users
    usersArray.sort((a, b) => {
      switch (sortOption) {
        case "name-asc":
          return a.username.localeCompare(b.username);
        case "name-desc":
          return b.username.localeCompare(a.username);
        case "score-desc":
          return a.scorePercentage - b.scorePercentage;
        case "score-asc":
          return b.scorePercentage - a.scorePercentage;
        default:
          return 0;
      }
    });

    return usersArray;
  }, [submissions, searchTerm, sortOption]);
  
  // Pagination for student list - optimized with useMemo
  const { currentUsers, totalPages, groupedByUser, userIds } = useMemo(() => {
    const indexOfLastUser = currentPage * itemsPerPage;
    const indexOfFirstUser = indexOfLastUser - itemsPerPage;
    const paginatedUsers = filteredAndSortedUsers.slice(indexOfFirstUser, indexOfLastUser);
    const pages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
    
    // For backward compatibility with existing code
    const userMap = filteredAndSortedUsers.reduce((acc, user) => {
      acc[user.userId] = user;
      return acc;
    }, {});
    
    const ids = Object.keys(userMap);
    
    return {
      currentUsers: paginatedUsers,
      totalPages: pages,
      groupedByUser: userMap,
      userIds: ids
    };
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  // Download CSV Data - optimized with useCallback
  const downloadCSV = useCallback(() => {
    if (userIds.length === 0) {
      alert("No data to download.");
      return;
    }

    let csvRows = ["Username,Total Score,Max Score,Percentage"];

    userIds.forEach((uid) => {
      const group = groupedByUser[uid];
      // Use pre-calculated values if available
      const userTotalMarks = group.totalMarks !== undefined ? 
        group.totalMarks : 
        group.items.reduce((acc, s) => acc + (parseFloat(s.markAwarded) || 0), 0);
        
      const userTotalMaxMarks = group.totalMaxMarks !== undefined ? 
        group.totalMaxMarks : 
        group.items.reduce((acc, s) => acc + (parseFloat(s.maxMark) || 0), 0);
        
      const userPercentage = userTotalMaxMarks > 0 ? 
        ((userTotalMarks / userTotalMaxMarks) * 100).toFixed(2) : "N/A";

      const usernameSafe = `"${group.username.replace(/"/g, '""')}"`;
      csvRows.push(`${usernameSafe},${userTotalMarks},${userTotalMaxMarks},${userPercentage}`);
    });

    const csvContent = csvRows.join("\n");

    // Use more efficient way to download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.style.display = "none";
    link.download = `quiz_${quizCode}_results.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [userIds, groupedByUser, quizCode]);

  // Toggle student expansion - optimized with useCallback
  const toggleStudentExpansion = useCallback((userId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  }, []);

  // Toggle question expansion - new function
  const toggleQuestionExpansion = useCallback((questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  }, []);

  // Handle manual mark updates - optimized with useCallback
  const handleMarkChange = useCallback((submissionId, value) => {
    setManualMarks((prev) => ({
      ...prev,
      [submissionId]: value,
    }));
  }, []);

  // Save manual mark updates to Firestore - optimized with useCallback
  const saveManualMark = useCallback(async (submissionId) => {
    try {
      const mark = manualMarks[submissionId];
      if (mark === undefined) return;

      const submissionRef = doc(db, "submissions", submissionId);
      await updateDoc(submissionRef, {
        markAwarded: mark,
      });

      // Update local state efficiently
      setSubmissions((prev) =>
        prev.map((sub) =>
          sub.id === submissionId ? { ...sub, markAwarded: mark } : sub
        )
      );

      // Use a less intrusive notification method
      console.log("Mark updated successfully!");
    } catch (error) {
      console.error("Error updating mark:", error);
      alert("Failed to update mark. Please try again.");
    }
  }, [manualMarks]);

  // Render the component
  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="improved-header">
          <div className="nav-left">
            <div className="logo-container">
              <img src="/newlogo.png" alt="Logo" />
              <h1>Quiz Responses</h1>
            </div>
          </div>
          <div className="nav-right">
            <button onClick={() => navigate('/dashboard')} className="nav-button">
              Dashboard
            </button>
            <button onClick={() => navigate('/view-quizzes')} className="nav-button">
              View Quizzes
            </button>
            <button onClick={() => navigate(-1)} className="back-button">
              Back
            </button>
          </div>
        </div>

        <div className="main-content">
          {/* Quiz Code Input Card */}
          <div className="card">
            <h2>View Quiz Responses</h2>
            
            {quizTitle && (
              <div className="quiz-title">
                Quiz: {quizTitle}
              </div>
            )}
            
            <div className="input-group">
              <label className="input-label">Quiz Code:</label>
              <input
                type="text"
                value={quizCode}
                onChange={(e) => setQuizCode(e.target.value)}
                placeholder="Enter quiz code"
                className="text-input"
              />
            </div>
            
            {/* Search and Sort Controls */}
            {submissions.length > 0 && (
              <div className="search-sort-container">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search students..."
                  className="search-input"
                />
                
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="sort-select"
                >
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="score-asc">Score (High-Low)</option>
                  <option value="score-desc">Score (Low-High)</option>
                </select>
              </div>
            )}
            
            <div className="button-container">
              <button
                onClick={loadQuizResponses}
                disabled={loading || marking}
                className="primary-button"
              >
                Load Responses
              </button>
              
              {submissions.length > 0 && (
                <>
                  <button
                    onClick={markAllWithAI}
                    disabled={loading || marking}
                    className="red-button"
                  >
                    Mark All with AI
                  </button>
                  
                  <button
                    onClick={markAllUnmarkedWithAI}
                    disabled={loading || marking}
                    className="blue-button"
                  >
                    Mark Unmarked with AI
                  </button>
                  
                  <button
                    onClick={downloadCSV}
                    disabled={loading || marking}
                    className="dark-button"
                  >
                    Download CSV
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p className="loading-text">Loading quiz responses...</p>
            </div>
          )}

          {/* Marking Progress */}
          {marking && markingProgress.inProgress && (
            <div className="card">
              <h3>AI Marking Progress</h3>
              
              {markingProgress.verificationPhase ? (
                <>
                  <div className="progress-phase">Verification Phase</div>
                  <div className="progress-container">
                    <div className="progress-header">
                      <span>Verifying batch {markingProgress.currentBatch} of {markingProgress.totalBatches}</span>
                      <span>{markingProgress.verificationCompleted} submissions verified</span>
                    </div>
                    
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar verification-bar"
                        style={{ width: `${(markingProgress.verificationCompleted / Math.max(1, markingProgress.verificationCompleted + markingProgress.verificationFailed)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {markingProgress.verificationFailed > 0 && (
                    <p className="error-text">
                      Failed verifications: {markingProgress.verificationFailed}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="progress-phase">Initial Processing Phase</div>
                  <div className="progress-container">
                    <div className="progress-header">
                      <span>Processing batch {markingProgress.currentBatch} of {markingProgress.totalBatches}</span>
                      <span>{markingProgress.completed} of {markingProgress.total} completed</span>
                    </div>
                    
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar"
                        style={{ width: `${(markingProgress.completed / markingProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {markingProgress.failed > 0 && (
                    <p className="error-text">
                      Failed submissions: {markingProgress.failed}
                      {markingProgress.retryAttempts > 0 && ` (Retry attempts: ${markingProgress.retryAttempts})`}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Student Responses */}
          {!loading && !marking && submissions.length > 0 && (
            <div className="card">
              <h3>Student Responses</h3>
              
              {currentUsers.length === 0 ? (
                <p className="empty-state-text">
                  No students match your search criteria.
                </p>
              ) : (
                currentUsers.map((user) => {
                  const userSubs = user.items;
                  const userTotalMarks = userSubs.reduce(
                    (acc, s) => acc + (parseFloat(s.markAwarded) || 0),
                    0
                  );
                  const userTotalMaxMarks = userSubs.reduce(
                    (acc, s) => acc + (parseFloat(s.maxMark) || 0),
                    0
                  );
                  const userPercentage =
                    userTotalMaxMarks > 0
                      ? ((userTotalMarks / userTotalMaxMarks) * 100).toFixed(2)
                      : "N/A";
                  
                  // Add score color class based on percentage
                  let scoreColorClass = "score-neutral";
                  if (userPercentage !== "N/A") {
                    const percent = parseFloat(userPercentage);
                    if (percent >= 80) scoreColorClass = "score-excellent";
                    else if (percent >= 60) scoreColorClass = "score-good";
                    else if (percent >= 40) scoreColorClass = "score-average";
                    else scoreColorClass = "score-poor";
                  }
                  
                  const isExpanded = expandedStudents[user.userId] || false;
                  
                  return (
                    <div
                      key={user.userId}
                      className="student-card"
                    >
                      <div
                        className="student-header"
                        onClick={() => toggleStudentExpansion(user.userId)}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span className={`student-toggle ${isExpanded ? 'expanded' : ''}`}>▶</span>
                          <h4 className="student-name">{user.username}</h4>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                          <span className={`student-score ${scoreColorClass}`}>
                            <span className="score-label">Score:</span> {userTotalMarks}/{userTotalMaxMarks} 
                            <span className="score-percent">({userPercentage}%)</span>
                          </span>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="student-details">
                          <div className="student-responses-container">
                            {userSubs.map((sub) => (
                              <div key={sub.id} className="response-item">
                                <div 
                                  className="response-header"
                                  onClick={(e) => {
                                    // Prevent the click from triggering when clicking on the input or button
                                    if (
                                      e.target.tagName !== 'INPUT' && 
                                      e.target.tagName !== 'BUTTON' &&
                                      !e.target.closest('.mark-input-container') &&
                                      !e.target.closest('.save-button')
                                    ) {
                                      toggleQuestionExpansion(sub.id);
                                    }
                                  }}
                                >
                                  <div className="response-title-container">
                                    <span className={`question-toggle ${expandedQuestions[sub.id] ? 'expanded' : ''}`}>▶</span>
                                    <div className="response-title">Question #{userSubs.indexOf(sub) + 1}</div>
                                  </div>
                                  <div className="mark-controls">
                                    <div className="mark-input-container">
                                      <span className="mark-label">Mark:</span>
                                      <input
                                        type="number"
                                        value={manualMarks[sub.id] || ""}
                                        onChange={(e) => handleMarkChange(sub.id, e.target.value)}
                                        className="mark-input"
                                        min="0"
                                        max={sub.maxMark}
                                      />
                                      <div className="max-mark-container">
                                        <span className="max-mark-slash">/</span>
                                        <span className="max-mark">{sub.maxMark}</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent triggering collapse
                                        saveManualMark(sub.id);
                                      }}
                                      className="save-button"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                                
                                {expandedQuestions[sub.id] && (
                                  <div className="response-content">
                                    <div className="response-row">
                                      <div className="response-cell question-cell">
                                        <div className="cell-label">Question:</div>
                                        <div className="truncated-text question-text">
                                          {sub.questionDisplay}
                                          {sub.questionImageURL && (
                                            <div className="question-image-container">
                                              <img
                                                src={sub.questionImageURL}
                                                alt="Question"
                                                className="question-image"
                                                loading="lazy"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="response-cell answer-cell">
                                        <div className="cell-label">Student Answer:</div>
                                        <div className="truncated-text student-answer">
                                          {sub.userAnswer}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="response-row">
                                      <div className="response-cell scheme-cell">
                                        <div className="cell-label">Mark Scheme:</div>
                                        <div className="truncated-text mark-scheme">
                                          {sub.markSchemeAnswer}
                                        </div>
                                      </div>
                                      <div className="response-cell feedback-cell">
                                        <div className="cell-label">Feedback:</div>
                                        <div className="truncated-text feedback-text">
                                          {sub.feedback}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              
              {/* Pagination Controls */}
              {filteredAndSortedUsers.length > itemsPerPage && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Prev
                  </button>
                  
                  <span className="pagination-text">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Last
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!loading && !marking && submissions.length === 0 && (
            <div className="empty-state">
              <p className="empty-state-text">
                Enter a quiz code and click "Load Responses" to view student submissions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewQuizResponses;
